import { useMemo } from 'react';

import {
  mapParticipantDetailAddress,
  mapParticipantDetailInvoice,
  mapParticipantDetailOrderItems,
  mapParticipantStoreShare,
  resolveParticipantDetailStatus,
} from '../helpers/participantOrderDetail.utils';
import { useGetParticipantOrderQuery } from '@/api/ParticipantHqQuery';
import type { IParticipantHqOrderDetail } from '@/api/ParticipantHqQuery';
import type { IInvoice } from '@/common/interfaces/invoice.interface';
import type { IAddress, IOrderItem } from '@/common/interfaces/order.interface';
import type { MostRecentModel } from '@/common/interfaces/mostRecent.interface';
import type { MostRecentActivityStatus } from '@/modules/MostRecent/MostRecentActivity';
import type { IOrderDetailStoreShare } from '@/modules/Orders/OrderDetail/OrderDetail';

export interface IParticipantOrderView {
  order: IParticipantHqOrderDetail;
  orderItems: IOrderItem[];
  address: IAddress;
  invoice: IInvoice;
  status: MostRecentActivityStatus;
  /** Order details stay hidden until the store tips. */
  isTipped: boolean;
  /** Store closed without meeting its minimum. */
  isFailedOrder: boolean;
  /** Order number once tipped, participant id before that. */
  displayId: string;
  /** Share card data while the store is still live; null once it can't be shared. */
  storeShare: IOrderDetailStoreShare | null;
  headerCode: string;
  headerData: MostRecentModel;
}

const buildParticipantOrderView = (order: IParticipantHqOrderDetail): IParticipantOrderView => {
  const orderItems = mapParticipantDetailOrderItems(order);
  const address = mapParticipantDetailAddress(order);
  const invoice = mapParticipantDetailInvoice(order);
  const status = resolveParticipantDetailStatus(order);
  const isTipped = Boolean(order.storeTipped);
  const isFailedOrder = Boolean(order.isFailedOrder);
  const displayId = order.orderNumber ? String(order.orderNumber) : String(order.participantId);

  return {
    order,
    orderItems,
    address,
    invoice,
    status,
    isTipped,
    isFailedOrder,
    displayId,
    storeShare: mapParticipantStoreShare(order),
    headerCode: isTipped && order.orderNumber ? `#${order.orderNumber}` : '',
    headerData: {
      orderId: displayId,
      orderTitle: order.storeName,
      orderItems,
      address,
      invoice,
      status,
      fromDeliveryDate: order.fromDeliveryDate || undefined,
      toDeliveryDate: order.toDeliveryDate || undefined,
      dueDate: order.dueDate || undefined,
      guaranteeDelivery: order.guaranteeDelivery,
    },
  };
};

/**
 * Loads one participant order and shapes it for the HQ order components.
 */
export const useParticipantOrderDetail = (participantId?: number | string) => {
  const { data, isLoading, isFetching, isError } = useGetParticipantOrderQuery(
    participantId ?? '',
    { skip: !participantId }
  );

  const view = useMemo(
    () => (data?.order ? buildParticipantOrderView(data.order) : null),
    [data?.order]
  );

  return {
    view,
    isLoading: isLoading || isFetching,
    isError,
  };
};

export default useParticipantOrderDetail;
